import { Overlay } from '@angular/cdk/overlay';
import { Component, ElementRef, Injector, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { IPickerOption } from './nv-multi-picker.interface';
import { NvMultipickerMenuService } from './nv-multi-picker.service';

@Component({
  selector: 'nv-multi-picker',
  template: require('./nv-multi-picker.component.html'),
  styles: [require('./nv-multi-picker.component.scss')],
})
export class NvMultiPickerComponent {
  /**
   *
   *
   * Array of PickerOptions
   *  ```js
   *    interface IPickerOption {
   *      key: string,
   *      human: string
   *    }
   *  ```
   *
   * @required
   */
  @Input() options: IPickerOption[] = [];

  /**
   *
   * Array of keys.
   * This component will modify this array internally. Use 2-way binding to get updates
   *
   * e.g. `[(selectedOptions)]="myArrayOfSelectedOptions"`
   *
   * @required
   */
  @Input() selectedOptions: string[] = [];

  /**
   *
   * The base color of the pill
   */
  @Input() color: string = 'blue';

  /**
   *
   * When `true`, this sets the text to `color`, and the background to a light version of `color`
   */
  @Input() isInverted: boolean = false;

  /**
   *
   * When `true`, this disables the pill component
   */
  @Input() isDisabled: boolean = false;

  /**
   *
   * Defines whether the option to **select all** options exists
   */
  @Input() hasAllButton: boolean = true;

  /**
   *
   * Defines whether the option to **clear all** selected options exists
   */
  @Input() hasClearButton: boolean = true;

  /**
   *
   * Defines the text displayed when no selections have been made
   */
  @Input() placeholder: string = 'None';

  @Input() hasSearchBox: boolean = true;

  private _menuService: NvMultipickerMenuService;

  constructor(private overlay: Overlay, private injector: Injector, private elementRef: ElementRef) {
    this._menuService = new NvMultipickerMenuService(this.overlay, this.injector);
  }

  private reverseCount = 5;
  pulse = false;

  // TODO - turn selectedOptions array to Object (for performance)
  //
  get pillLabel(): string {
    if (!this.selectedOptions || this.selectedOptions.length === 0) {
      return this.placeholder;
    } else if (this.selectedOptions.length === this.options.length) {
      return 'All';
    } else if (this.selectedOptions.length === 1) {
      return `${this.findOptionByKey(this.selectedOptions[0]).human}`;
    } else if (this.options.length - this.selectedOptions.length <= this.reverseCount) {
      return `All but ${this.options.length - this.selectedOptions.length}`;
    } else {
      return `${this.selectedOptions.length} selected`;
    }
  }

  get placeholderSelected(): boolean {
    return (
      this.selectedOptions.length === 0 ||
      (this.selectedOptions.length === 1 && this.selectedOptions[0] === this.placeholder)
    );
  }

  get _selectedOptionsObject(): object {
    const _obj = {};
    this.selectedOptions.forEach((opt: string) => {
      _obj[opt] = true;
    });
    return _obj;
  }

  ngOnDestroy(): void {
    if (this._menuService) {
      this._menuService.closeMenu();
    }
  }

  updateSelectedOptionsArray(value: string) {
    this.pulsePill();
    switch (value) {
      case 'all':
        this.options.forEach(opt => {
          if (!this.isKeySelected(opt.key)) {
            this.selectedOptions.push(opt.key);
          }
        });
        break;
      case 'clear':
        this.selectedOptions.length = 0; // empty the array
        break;
      default:
        if (this.isKeySelected(value)) {
          this.selectedOptions.splice(this.indexOfKey(value), 1);
        } else {
          this.selectedOptions.push(value);
        }
        break;
    }
  }

  private findOptionByKey(key: string) {
    return this.options.find(opt => opt.key === key);
  }

  private indexOfKey(key: string) {
    return this.selectedOptions.indexOf(key);
  }

  private isKeySelected(key: string): boolean {
    return this.selectedOptions.includes(key);
  }

  private pulsePill() {
    this.pulse = true;
    setTimeout(() => {
      this.pulse = false;
    }, 100);
  }

  toggleMenu() {
    if (!this.isDisabled) {
      // Create a new menu instance each time it's opened
      // This is more performant when multiple pickers are on page
      // It also allows us to more easily change the options inside the picker
      this._menuService.toggleMenu(this.elementRef.nativeElement, {
        options: this.options,
        hasAllButton: this.hasAllButton,
        hasClearButton: this.hasClearButton,
        hasSearchBox: this.hasSearchBox,
        initialSelection: this._selectedOptionsObject, // pass in separate from the obeservable
        selectedOption$: new Subject<string[]>(),
      });

      // Subscribe to changes emitted from the menu
      if (this._menuService.isOpen) {
        this._menuService.menuRef.updateSelection$.subscribe(optionKey => {
          this.updateSelectedOptionsArray(optionKey); // update the internal value
          const optionsObject = this._selectedOptionsObject;
          this._menuService.menuRef.setSelection(optionsObject); // send that value back down to the menu
        });
      }
    }
  }
}
